import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { StepIndicatorContext } from '../context/StepIndicatorContext';
import { jsx as _jsx } from "react/jsx-runtime";
const setsAreEqual = (a, b) => a.size === b.size && [...a].every(item => b.has(item));
const DEFAULT_FIRST_STEP = 1;
export const StepIndicatorProvider = ({
  children,
  enforceStepOrder = false,
  navigationAdapter,
  progressAdapter,
  steps = [],
  totalSteps,
  firstStep = DEFAULT_FIRST_STEP,
  isLoading,
  onStepChange
}) => {
  var _activeSteps$find;
  const {
    currentStep,
    navigate,
    replace
  } = navigationAdapter;
  const replaceStep = replace !== null && replace !== void 0 ? replace : navigate;
  const [completedSteps, setCompletedSteps] = useState(new Set());
  const [visitedSteps, setVisitedSteps] = useState(new Set());
  const [isProgressLoaded, setIsProgressLoaded] = useState(!progressAdapter);
  const [isStepIndicatorMenuOpen, setIsStepIndicatorMenuOpen] = useState(false);
  const [pendingNavigationStep, setPendingNavigationStep] = useState(null);
  const [dynamicEmptyStepNames, setDynamicEmptyStepNames] = useState(new Set());
  const [visitedStepNames, setVisitedStepNames] = useState(new Set());
  const syncedFromAdapter = useRef(false);
  const latestRef = useRef({
    currentStep,
    totalSteps,
    completedSteps,
    visitedSteps
  });
  const visitedStepNamesRef = useRef(visitedStepNames);
  visitedStepNamesRef.current = visitedStepNames;

  // Sync from adapter when async load completes
  useEffect(() => {
    if (progressAdapter !== null && progressAdapter !== void 0 && progressAdapter.isLoaded && !syncedFromAdapter.current) {
      syncedFromAdapter.current = true;
      setCompletedSteps(new Set(progressAdapter.completedSteps));
      setVisitedSteps(new Set(progressAdapter.visitedSteps));
      setVisitedStepNames(new Set(progressAdapter.visitedStepNames));
      setIsProgressLoaded(true);
    }
  }, [progressAdapter === null || progressAdapter === void 0 ? void 0 : progressAdapter.isLoaded, progressAdapter === null || progressAdapter === void 0 ? void 0 : progressAdapter.visitedSteps, progressAdapter === null || progressAdapter === void 0 ? void 0 : progressAdapter.completedSteps, progressAdapter === null || progressAdapter === void 0 ? void 0 : progressAdapter.visitedStepNames]);
  const reportEmptySteps = useCallback(emptyStepNames => {
    setDynamicEmptyStepNames(prev => {
      if (setsAreEqual(prev, emptyStepNames)) {
        return prev;
      }
      return emptyStepNames;
    });
  }, []);
  const activeSteps = useMemo(() => steps.filter(step => step.stepNumber === firstStep || !dynamicEmptyStepNames.has(step.name) || visitedStepNames.has(step.name)).map((step, index) => ({
    ...step,
    stepNumber: index + firstStep
  })), [steps, dynamicEmptyStepNames, visitedStepNames, firstStep]);

  // Use activeSteps-derived total only when steps prop is provided (DBF).
  // Falls back to totalSteps prop for flows that don't pass steps (RF and others).
  const computedTotalSteps = steps.length > 0 ? activeSteps.length + (firstStep - 1) : totalSteps;
  latestRef.current = {
    currentStep,
    totalSteps: computedTotalSteps,
    completedSteps,
    visitedSteps
  };

  // Accessibility guard + visited tracking
  useEffect(() => {
    if (!isProgressLoaded) {
      return;
    }
    if (enforceStepOrder) {
      const isAccessible = currentStep === firstStep || latestRef.current.completedSteps.has(currentStep - 1);
      if (!isAccessible) {
        // If user try to access non-visited step then redirect them to configured first step
        replaceStep(firstStep);
        return;
      }
    }
    if (!latestRef.current.visitedSteps.has(currentStep)) {
      setVisitedSteps(new Set(latestRef.current.visitedSteps).add(currentStep));
    }
  }, [currentStep, isProgressLoaded, firstStep, enforceStepOrder, replaceStep]);
  const isStepCompleted = useCallback(step => latestRef.current.completedSteps.has(step), []);
  const isStepVisited = useCallback(step => latestRef.current.visitedSteps.has(step), []);
  const isStepAccessible = useCallback(step => step === firstStep || latestRef.current.completedSteps.has(step - 1), [firstStep]);
  const markStepCompleted = useCallback(step => {
    if (latestRef.current.completedSteps.has(step)) {
      return;
    }
    setCompletedSteps(new Set(latestRef.current.completedSteps).add(step));
  }, []);
  const handleNextStep = useCallback(() => {
    const {
      currentStep,
      totalSteps
    } = latestRef.current;
    const nextStep = currentStep + 1;
    if (nextStep > totalSteps) {
      return;
    }
    markStepCompleted(currentStep);
    navigate(nextStep);
    onStepChange === null || onStepChange === void 0 || onStepChange(nextStep);
  }, [navigate, markStepCompleted, onStepChange]);
  const handlePrevStep = useCallback(() => {
    const prevStep = latestRef.current.currentStep - 1;
    if (prevStep < firstStep) {
      return;
    }
    navigate(prevStep);
    onStepChange === null || onStepChange === void 0 || onStepChange(prevStep);
  }, [navigate, firstStep, onStepChange]);
  const navigateToStep = useCallback(step => {
    if (!isStepAccessible(step)) {
      return;
    }
    navigate(step);
    onStepChange === null || onStepChange === void 0 || onStepChange(step);
  }, [navigate, isStepAccessible, onStepChange]);
  const currentStepName = (_activeSteps$find = activeSteps.find(s => s.stepNumber === currentStep)) === null || _activeSteps$find === void 0 ? void 0 : _activeSteps$find.name;

  // Track visited step names for the empty-step "already purchased" check.
  // Uses currentStepName (string primitive) instead of activeSteps (array) to avoid
  // triggering on every activeSteps reference change (which would create a cycle).
  // Gated on isProgressLoaded so it never runs before the async load + sync completes.
  useEffect(() => {
    if (!isProgressLoaded) {
      return;
    }
    if (currentStepName && !visitedStepNamesRef.current.has(currentStepName)) {
      setVisitedStepNames(new Set([...visitedStepNamesRef.current, currentStepName]));
    }
  }, [currentStepName, isProgressLoaded]);

  // Single writer for persistence: whenever progress changes (after the initial load +
  // sync), persist the current snapshot. Centralising the write here avoids the races
  // and stale overwrites that came from saving separately at each mutation site.
  useEffect(() => {
    var _progressAdapter$onPr;
    if (!isProgressLoaded) {
      return;
    }
    progressAdapter === null || progressAdapter === void 0 || (_progressAdapter$onPr = progressAdapter.onProgressChange) === null || _progressAdapter$onPr === void 0 || _progressAdapter$onPr.call(progressAdapter, completedSteps, visitedSteps, visitedStepNames);
  }, [isProgressLoaded, completedSteps, visitedSteps, visitedStepNames, progressAdapter]);
  const isCurrentStepEmptyAndVisited = !!currentStepName && currentStep !== firstStep && dynamicEmptyStepNames.has(currentStepName) && visitedStepNames.has(currentStepName);
  const setIsOnSectionOne = useCallback(goToSectionOne => {
    if (goToSectionOne) {
      replaceStep(firstStep);
    } else if (currentStep === firstStep) {
      navigate(currentStep + 1);
    }
  }, [currentStep, firstStep, replaceStep, navigate]);
  const value = useMemo(() => ({
    currentStep,
    totalSteps: computedTotalSteps,
    steps: activeSteps,
    isProgressLoaded,
    CONFIGURED_FIRST_STEP: firstStep,
    isOnFirstStep: currentStep === firstStep,
    isOnLastStep: currentStep >= computedTotalSteps,
    isOnSectionOne: currentStep === firstStep,
    setIsOnSectionOne,
    handleNextStep,
    handlePrevStep,
    navigateToStep,
    markStepCompleted,
    isStepCompleted,
    isStepVisited,
    isStepAccessible,
    isStepIndicatorMenuOpen,
    setIsStepIndicatorMenuOpen,
    pendingNavigationStep,
    setPendingNavigationStep,
    isLoading,
    reportEmptySteps,
    isCurrentStepEmptyAndVisited
  }), [setIsOnSectionOne, currentStep, computedTotalSteps, activeSteps, isProgressLoaded, firstStep, handleNextStep, handlePrevStep, navigateToStep, markStepCompleted, isStepCompleted, isStepVisited, isStepAccessible, isStepIndicatorMenuOpen, pendingNavigationStep, isLoading, reportEmptySteps, isCurrentStepEmptyAndVisited]);
  return /*#__PURE__*/_jsx(StepIndicatorContext.Provider, {
    value: value,
    children: children
  });
};
//# sourceMappingURL=StepIndicatorProvider.js.map